import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { useSeo } from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";
import { siteInfo } from "@/mocks/site";

const quickNav = [
  { icon: "ri-lock-2-line", label: "匿名与隐私", anchor: "faq-anonymous" },
  { icon: "ri-file-search-line", label: "审核与采用", anchor: "faq-review" },
  { icon: "ri-mail-send-line", label: "渠道与回复", anchor: "faq-channel" },
];

export default function FaqPage() {
  useSeo({
    title: "常见问题 | 网络爆料匿名投稿怎么投 - 直曝",
    description:
      "投稿要不要注册？匿名会不会被认出来？多久能收到回复？直曝整理投稿人最常问的八个问题，从匿名保护、审核规则到邮箱与后台私信渠道，一次说清楚。",
    keywords: "网络爆料,网络爆料匿名,网络爆料投稿,网络爆料邮箱,匿名投稿常见问题",
    path: "/faq",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://wzwzww.cn/" },
          { "@type": "ListItem", position: 2, name: "常见问题", item: "https://wzwzww.cn/faq" },
        ],
      },
    ],
  });

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: "常见问题" }]} />
            <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              投稿常见问题
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              关于匿名、审核与材料去向，我们把最常被追问的八个问题整理在这里。看完还有疑问，可以直接联系编辑。
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8">
            <div id="faq-anonymous" className="scroll-mt-28">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-lock-2-line text-base" aria-hidden="true"></i>
                </span>
                匿名与隐私
              </h2>
              <div className="mt-4">
                <FaqAccordion items={faqs.slice(0, 3)} defaultOpen={0} />
              </div>
            </div>

            <div id="faq-review" className="scroll-mt-28 mt-12">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-file-search-line text-base" aria-hidden="true"></i>
                </span>
                审核与采用
              </h2>
              <div className="mt-4">
                <FaqAccordion items={faqs.slice(3, 6)} />
              </div>
            </div>

            <div id="faq-channel" className="scroll-mt-28 mt-12">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-mail-send-line text-base" aria-hidden="true"></i>
                </span>
                渠道与回复
              </h2>
              <div className="mt-4">
                <FaqAccordion items={faqs.slice(6)} />
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 flex flex-col gap-5">
              <nav className="rounded-lg border border-background-200 bg-background-100 p-5" aria-label="问题分类导航">
                <h2 className="font-heading text-sm font-bold text-foreground-950">按主题查看</h2>
                <ul className="mt-3.5 space-y-2">
                  {quickNav.map((item) => (
                    <li key={item.anchor}>
                      <a
                        href={`#${item.anchor}`}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-200 hover:text-foreground-950 transition-colors cursor-pointer"
                      >
                        <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-primary-600`} aria-hidden="true"></i>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="rounded-lg bg-primary-600 p-5 text-background-50">
                <h2 className="font-heading text-base font-bold">还有别的问题？</h2>
                <p className="mt-2.5 text-sm text-primary-50/90 leading-relaxed">
                  直接写信给编辑，我们会尽量在三个工作日内回复。邮箱：
                </p>
                <p className="mt-3 text-sm break-all">
                  <a href={`mailto:${siteInfo.editorEmail}`} className="text-accent-200 hover:underline cursor-pointer">
                    {siteInfo.editorEmail}
                  </a>
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-primary-700 text-sm font-label font-medium whitespace-nowrap hover:bg-accent-100 transition-colors cursor-pointer"
                >
                  前往联系页面
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">准备好投稿了？</h2>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  不需要注册，联系方式可以留空。
                </p>
                <Link
                  to="/submit"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  打开投稿通道
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}